import React, { Dispatch, SetStateAction, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CompontHeader } from './compont-header';
import { tabItems, TabName } from '../data/tab-items';
import { ChangeCards } from './change-cards';
import popularDishes from '../data/dishes/popular.json';

export function Menue() {
  const [position, setPosition] = useState<Position>({
    left: 0,
    width: 0,
    opacity: 0,
  });

  const [activeTab, setActiveTab] = useState<TabName | null>(null);
  const [hoveredTab, setHoveredTab] = useState<TabName | null>(null);

  const getContentForTab = (id: TabName | null) => {
    if (!id) return <ChangeCards dishes={popularDishes} />;
    return tabItems.find((item) => item.id === id)?.content;
  };

  return (
    <div
      id="menue"
      className="pt-[84px] relative mx-auto w-full max-w-7xl gap-8 px-4"
    >
      <div className="pb-6">
        <CompontHeader text="Speisekarte" />
      </div>
      <p className="text-center text-3xl pb-6">
        Unsere {!activeTab && 'beliebtesten'} Gerichte
      </p>
      {/* Wrapper for overflow handling */}
      <div className="overflow-x-auto pb-2 lg:pb-6">
        <ul
          onMouseLeave={() => {
            if (!activeTab) {
              setPosition((pv) => ({
                ...pv,
                opacity: 0,
              }));
            } else {
              const activeElement = document.querySelector(
                `[data-tab="${activeTab}"]`,
              ) as HTMLLIElement;
              if (activeElement) {
                const { width } = activeElement.getBoundingClientRect();
                setPosition({
                  left: activeElement.offsetLeft,
                  width,
                  opacity: 1,
                });
              }
            }
            setHoveredTab(null);
          }}
          className="relative mx-auto flex w-fit min-w-fit rounded-full border-2 border-[#FAED04] bg-white p-1"
        >
          {tabItems.map(({ id, icon }) => (
            <Tab
              key={id}
              id={id}
              setPosition={setPosition}
              icon={icon}
              setActiveTab={setActiveTab}
              isActive={activeTab === id}
              setHoveredTab={setHoveredTab}
              isHovered={hoveredTab === id}
              hoveredTab={hoveredTab}
            >
              {id}
            </Tab>
          ))}

          <motion.li
            animate={{
              ...position,
            }}
            className="absolute z-0 h-7 rounded-full bg-black md:h-12"
          />
        </ul>
      </div>

      <div className="mt-4">{getContentForTab(activeTab)}</div>
    </div>
  );
}

const Tab = ({
  children,
  id,
  setPosition,
  icon,
  setActiveTab,
  isActive,
  setHoveredTab,
  isHovered,
  hoveredTab,
}: {
  children: React.ReactNode;
  id: TabName;
  setPosition: Dispatch<SetStateAction<Position>>;
  icon: JSX.Element;
  setActiveTab: Dispatch<React.SetStateAction<TabName | null>>;
  isActive: boolean;
  setHoveredTab: Dispatch<React.SetStateAction<TabName | null>>;
  isHovered: boolean;
  hoveredTab: TabName | null;
}) => {
  const ref = useRef<null | HTMLLIElement>(null);

  return (
    <li
      ref={ref}
      data-tab={id}
      onMouseEnter={() => {
        if (!ref?.current) return;
        const { width } = ref.current.getBoundingClientRect();
        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
        setHoveredTab(id);
      }}
      onClick={() => {
        setActiveTab(id);
        if (!ref?.current) return;
        const { width } = ref.current.getBoundingClientRect();
        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
      }}
      className={`relative z-10 flex cursor-pointer items-center gap-2 px-3 py-1.5 text-nowrap text-xs uppercase transition-colors md:px-5 md:py-3 md:text-base ${
        isHovered
          ? 'text-[#FAED04]'
          : isActive
          ? hoveredTab
            ? 'text-black'
            : 'text-[#FAED04]'
          : 'text-black'
      }`}
    >
      {icon} {children}
    </li>
  );
};

type Position = {
  left: number;
  width: number;
  opacity: number;
};
